import { App as AntApp, Button, Col, DatePicker, Form, Input, Row, Select, Space, Tag, Typography } from "antd";
import { CrownOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import dayjs, { type Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useApplyProfile, useReportError, type StudentProfile } from "../app/students.ts";
import { ItemList } from "./ItemList.tsx";

type Gender = components["schemas"]["Gender"];

const genders: Gender[] = ["Male", "Female"];
const fields = ["firstName", "lastName", "middleName", "dateOfBirth", "gender", "notes"] as const;
const NOTES_MAX = 2000;

interface InfoForm {
  firstName: string;
  lastName: string;
  middleName?: string;
  dateOfBirth?: Dayjs | null;
  gender?: Gender | null;
  notes?: string;
}

export function StudentInfoTab({ student, canManage }: { student: StudentProfile; canManage: boolean }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<InfoForm>();
  const applyProfile = useApplyProfile();
  const reportError = useReportError(form, fields);

  const save = useMutation({
    mutationFn: async (values: InfoForm) => {
      const { data, error, response } = await api.PUT("/students/{id}", {
        params: { path: { id: student.id } },
        body: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          middleName: values.middleName?.trim() || null,
          dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format("YYYY-MM-DD") : null,
          gender: values.gender ?? null,
          notes: values.notes?.trim() || null,
        },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (profile) => {
      await applyProfile(profile);
      void message.success(t("student.info.saved"));
    },
    onError: reportError,
  });

  const required = [{ required: true, whitespace: true, message: t("family.validation.required") }];
  return (
    <Row gutter={[32, 16]}>
      <Col xs={24} lg={14}>
        <Form<InfoForm>
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={!canManage}
          initialValues={{
            firstName: student.firstName,
            lastName: student.lastName,
            middleName: student.middleName ?? "",
            dateOfBirth: student.dateOfBirth ? dayjs(student.dateOfBirth) : null,
            gender: student.gender ?? null,
            notes: student.notes ?? "",
          }}
          onFinish={(values) => save.mutate(values)}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="lastName" label={t("family.lastName")} rules={required}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="firstName" label={t("family.firstName")} rules={required}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="middleName" label={t("student.info.middleName")}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="dateOfBirth"
                label={t("family.dateOfBirth")}
                rules={[{ validator: (_, value?: Dayjs | null) => (value && value.isAfter(dayjs(), "day") ? Promise.reject(new Error(t("student.info.futureDate"))) : Promise.resolve()) }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" disabledDate={(d) => d.isAfter(dayjs(), "day")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="gender" label={t("student.info.gender")}>
                <Select allowClear options={genders.map((g) => ({ value: g, label: t(`student.genders.${g}`) }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label={t("student.info.notes")} extra={t("student.info.notesHint")}>
            <Input.TextArea maxLength={NOTES_MAX} showCount autoSize={{ minRows: 3, maxRows: 8 }} />
          </Form.Item>
          {canManage && (
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t("family.save")}
            </Button>
          )}
        </Form>
      </Col>
      <Col xs={24} lg={10}>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          {t("student.info.guardians")}
        </Typography.Title>
        <ItemList
          size="small"
          items={student.guardians}
          itemKey={(guardian) => guardian.id}
          renderItem={(guardian) => ({
            content: (
              <Space orientation="vertical" size={0}>
                <Space wrap>
                  <span>{guardian.fullName}</span>
                  <Tag>{t(`family.relationships.${guardian.relationship}`)}</Tag>
                  {guardian.isPayer && (
                    <Tag color="gold" icon={<CrownOutlined />}>
                      {t("family.payer")}
                    </Tag>
                  )}
                </Space>
                <a href={`tel:${guardian.phone}`}>{guardian.phone}</a>
              </Space>
            ),
          })}
        />
        <Typography.Paragraph style={{ marginTop: 8 }}>
          <Link to={`/families/${student.familyId}`}>{t("student.info.openFamily")}</Link>
        </Typography.Paragraph>
      </Col>
    </Row>
  );
}
